import { themeColors } from '../../../../themeColors';
/**
 * @file   src\mobile\components\Onboarding\AgentProfile.js
 * @brief  This file is responsible for adding agent profile while onboarding.
 * @date   June, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import TextArea from "../../../../components/Common/TextArea";
import TextBox from "../../../../components/Common/TextBox";
import FldCloseIcon from "../../../images/round-close-icon.svg";
import {
  handleTextBoxChange,
  capitalizeString,
} from "../../../../actions/common";
import AddIcon from "../../../../images/v2/Add-icon.svg";
import {
  capitalizeFirstString,
  getFileType,
  toTitleCase,
  getLocalStorage,
  trimString,
  formatPhoneNumber,
  upperCaseEachSentenceFirst,
  removeLocalStorage,
} from "../../../../helpers/common";
import {
  alphaNumericValidation,
  licenseValidation,
  phoneNumberValidation,
  textValidations,
} from "../../../../helpers/validations";
import {
  AGENT_USER_FREE_PLAN,
  BIO_MAX_COUNT,
  ERROR_TEXT,
  TESTIMONIAL_MAX_COUNT,
} from "../../../../constants/common";
import { fetchUserDetails } from "../../../../actions/userprofile";
import { USER_INFO_KEY } from "../../../../constants/common";
import Loader from "../../../../components/Common/Loader";
import { getAgentDetails } from "../../../../services/agentClientsService";
import ValidationAlertIcon from "../../../images/validation-alert-icon.svg";
import SparkleBigIcon from "../../../images/sparkle-big-icon.svg";
import ProfImgPlaceholder from "../../../images/prof-img-placeholder.svg";
import ProfileImageCropper from "../ProfileImageCropper";
import { getGeoLocation } from "../../../../services/commonServices";

const AgentProfileDefault = (props) => {
  const dispatch = useDispatch();
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(sessionData, "userInfo.userId", "");
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const locationString = getLocalStorage("LocationString");
  const [formattedAddress, setFormattedAddress] = useState();
  const [locationList, setLocationList] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFNError] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameError, setLNError] = useState("");
  const [company, setCompany] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [license, setLicense] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [bio, setBio] = useState("");
  const [bioError, setBioError] = useState("");
  const [bioCount, setBioCount] = useState(0);
  const [imgError, setImgError] = useState("");
  const [reload, setReload] = useState(0);
  const supportedImageFormats = ["jpg", "jpeg", "png"];
  const { handleAgentDetailsSave } = props;
  const [isUploading, setIsUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [logoError, setLogoError] = useState("");
  const [clientTestimonial, setTestimonials] = useState([]);
  const [testimonialCounts, setTestimonialCounts] = useState([0]);
  const [logoUrl, setLogoUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [websiteUrlError, setWebsiteUrlError] = useState(false);
  const [youtubeUrlError, setYoutubeUrlError] = useState(false);
  const [facebookUrlError, setFacebookUrlError] = useState(false);
  const [instagramUrlError, setInstagramUrlError] = useState(false);
  const [bioCursorPos, setBioCursorPos] = useState(0);
  const bioRef = useRef();

  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [changedImage, setChangedImage] = useState(null);
  const [showPofileImageUploadModal, setShowPofileImageUploadModal] =
    useState(false);
  const [isSquareImage, setIsSquareImage] = useState(false);

  const ShowPofileImageUploadModalClose = () =>
    setShowPofileImageUploadModal(false);
  const ShowPofileImageUploadModalShow = (isSquareImage) => {
    setIsSquareImage(isSquareImage);
    setShowPofileImageUploadModal(true);
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserDetails());
    }
  }, [reload, userId]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[userDetailsFromStore?.length - 1]);
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    if (userId) {
      getAgentInfo(userId);
    }
  }, []);

  const getAgentInfo = async (userId) => {
    await getAgentDetails(userId).then((response) => {
      if (response) {
        const result = _.get(response, "result[0]", null);
        setLocation(_.get(result, "Location", null));
        setCompany(_.get(result, "Company", null));
        setLicense(_.get(result, "LicenseNo", null));
        setImageUrl(_.get(result, "Img", ""));
        setFirstName(_.get(result, "FirstName", null));
        setLastName(_.get(result, "LastName", null));
        setBio(_.get(result, "Bio", null));
        setFacebookUrl(_.get(result, "facebookUrl", null));
        setInstagramUrl(_.get(result, "instagramUrl", null));
        setWebsiteUrl(_.get(result, "websiteUrl", null));
        setYoutubeUrl(_.get(result, "youtubeUrl", null));
        setLogoUrl(_.get(result, "logoUrl", ""));
        setTestimonials(_.get(result, "clientTestimonial", []));
      }
    });
  };

  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (results?.length > 0 && formatted_address !== "United States") {
          if (locationString) {
            handleItemSelection(results[0]);
            removeLocalStorage("LocationString");
          } else {
            setLocationList(results);
          }
        }
      }
    });
  };

  const clearAddressList = () => {
    setLocationList([]);
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      getAddresseDetails({ reqPayload: reqPayload });
    } else {
      clearAddressList();
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      setLocation(value);
    }
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }
    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }
    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }
    if (city != undefined) {
      setFormattedAddress(city + ", " + state);
    } else {
      setFormattedAddress(formatted_address);
    }
    setLocation(city != undefined ? city + ", " + state : formatted_address);
    clearAddressList();
  };

  const handleFirstNameChange = (e) => {
    setFNError("");
    const value = e.target.value;
    if (trimString(value) !== "") {
      if (!textValidations(value.trim())) {
        setFNError("Invalid input");
      } else {
        setFirstName(value);
      }
    } else {
      setFNError("Enter FirstName");
      setFirstName("");
    }
  };

  const handleLastNameChange = (e) => {
    setLNError("");
    const value = e.target.value;
    if (trimString(value) !== "") {
      if (!textValidations(value.trim())) {
        setLNError("Invalid input");
      } else {
        setLastName(value);
      }
    } else {
      setLNError("Enter LastName");
      setLastName("");
    }
  };

  const handleCompany = (value) => {
    setCompanyError("");
    if (trimString(value) !== "") {
      if (!alphaNumericValidation(value.trim())) {
        setCompanyError("Invalid input");
      } else {
        setCompany(value);
      }
    } else {
      setCompanyError("Enter Company");
      setCompany("");
    }
  };

  const handleLicenseChange = (value) => {
    setLicenseError("");
    setLicense(value);
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      setLicenseError("Enter License Number");
    } else if (!licenseValidation(trimmedValue)) {
      //V2 change for license validation
      setLicenseError("Invalid input");
    }
  };

  const handlePhone = (value) => {
    setPhoneError("");
    if (trimString(value) !== "") {
      value = formatPhoneNumber(value);
      setPhone(value);
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  const handleUrlChange = (url, type) => {
    const trimmedUrl = url?.trim() || ""; // Ensure it's a string
    const websiteUrlRegex =
      /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;

    // Allow empty value, but validate only when there is input
    const isValid = trimmedUrl === "" || websiteUrlRegex.test(trimmedUrl);

    switch (type) {
      case "facebook":
        setFacebookUrl(trimmedUrl);
        setFacebookUrlError(trimmedUrl !== "" && !isValid); // Error only if input is invalid
        break;
      case "instagram":
        setInstagramUrl(trimmedUrl);
        setInstagramUrlError(trimmedUrl !== "" && !isValid);
        break;
      case "website":
        setWebsiteUrl(trimmedUrl);
        setWebsiteUrlError(trimmedUrl !== "" && !isValid);
        break;
      default:
        break;
    }
  };

  const handleYoutubeUrl = (url) => {
    const trimmedUrl = url.trim();
    setYoutubeUrl(trimmedUrl);
    setYoutubeUrlError(
      trimmedUrl === "" ? false : !validateYoutubeUrl(trimmedUrl),
    );
  };

  const validateYoutubeUrl = (url) => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)$/;
    return youtubeRegex.test(url);
  };

  const validatePhoneNumber = () => {
    setPhoneError("");
    if (trimString(phone) !== "") {
      if (!phoneNumberValidation(phone.trim())) {
        setPhoneError("Enter Valid Phone Number");
      } else {
        setPhone(phone);
      }
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  useEffect(() => {
    if (bioRef.current) {
      bioRef.current.setSelectionRange(bioCursorPos, bioCursorPos);
    }
  }, [bioCursorPos]);

  const handleBioChange = (e) => {
    setBioError("");
    const _bio = handleTextBoxChange(e);
    if (!textValidations(_bio)) {
      setBioError(ERROR_TEXT);
    }
    let value = capitalizeFirstString(_bio);
    value = upperCaseEachSentenceFirst(value);
    if (trimString(value) != "") {
      setBioCursorPos(e.target.selectionStart);
    }
    setBioCount(value.length);
    setBio(value);
  };

  const handleTestimonial = (e, index) => {
    const value = e.target.value;
    setTestimonials((prevTestimonials) =>
      prevTestimonials.map((testimonial, i) =>
        i === index ? value : testimonial,
      ),
    );
    setTestimonialCounts((prevCounts) =>
      prevCounts.map((count, i) => (i === index ? value.length : count)),
    );
  };

  const addTestimonial = () => {
    setTestimonials((prevTestimonials) => [...prevTestimonials, ""]);
    setTestimonialCounts((prevCounts) => [...prevCounts, 0]);
  };

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  const handleImageUpload = async (e, isLogo) => {
    const selectedFileType = getFileType(_.get(e.target.files[0], "name"));
    const file = e.target.files[0];

    setImgError("");
    let isValid = true;
    if (!_.includes(supportedImageFormats, selectedFileType)) {
      isValid = false;
      if (isLogo) setLogoError("Select Valid Logo");
      else setImgError("Select Valid Image");
    }
    if (isValid) {
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          setChangedImage(reader.result);
          ShowPofileImageUploadModalShow(isLogo);
        };
        reader.readAsDataURL(file);
        // Reset the file input
        e.target.value = null;
      }
    }
  };

  const checkValid = () => {
    if (userDetails?.PlanName != AGENT_USER_FREE_PLAN) {
      if (location && license && company && firstName && lastName && phone) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError ||
          phoneError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    } else {
      if (location && license && company && firstName && lastName) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError ||
          websiteUrlError ||
          youtubeUrlError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    }
  };

  const agentInfo = {
    firstName: firstName,
    lastName: lastName,
    company: company,
    location: location,
    licenseNo: license,
    img: imageUrl || "",
    phone: phone,
    logoUrl: logoUrl,
  };

  return (
    <div className="gitcha-main-wrapper pb-0 bg-grey-variant">
      {isUploading ? <Loader /> : ""}
      <div className="content-wrapper pt-0 h-100">
        <div className="inner-layout-header-new bg-transparent d-none">
          <div className="review-plan-header w-100">
            <div className="signup-login-header-top signup-header-txt clearfix"></div>
          </div>
        </div>

        <div className="inner-layout-content-new h-100">
         
          <div className="get-agent-wrap pt-5 clearfix">
            <h2>Tell us about yourself</h2>
          </div>
          <div className="d-block pe-3 ps-3">
            <div className="row">
              <div className="col-6 pe-2">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    First name <span>*</span>
                  </label>

                  <TextBox
                    name="firstName"
                    type="text"
                    value={toTitleCase(firstName)}
                    placeholder="First name"
                    notShowError={true}
                    className={
                      firstNameError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    onChange={(e) => {
                      handleFirstNameChange(e);
                    }}
                  />
                  {firstName && !firstNameError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setFirstName("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {firstNameError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{firstNameError}</div>
                </div>
              </div>
              <div className="col-6 ps-2">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Last name <span>*</span>
                  </label>

                  <TextBox
                    name="lastName"
                    type="text"
                    notShowError={true}
                    className={
                      lastNameError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(lastName)}
                    placeholder="Last name"
                    onChange={(e) => {
                      handleLastNameChange(e);
                    }}
                  />
                  {lastName && !lastNameError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLastName("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {lastNameError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{lastNameError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Phone Number <span> *</span>
                  </label>

                  <TextBox
                    name="phone"
                    notShowError={true}
                    type="text"
                    className={
                      phoneError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={phone}
                    placeholder="Phone Number"
                    onChange={(e) => handlePhone(e.target.value)}
                    onBlur={() => {
                      validatePhoneNumber();
                    }}
                    maxLength={14}
                    pattern="[0-9]*"
                    inputmode="numeric"
                  />
                  {phone && !phoneError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLocation("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {phoneError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{phoneError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Location <span>*</span>
                  </label>

                  <TextBox
                    name="location"
                    notShowError={true}
                    type="text"
                    className={
                      locationError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(location)}
                    placeholder="City, State"
                    onChange={(e) => {
                      handleLocationChange(e.target.value);
                    }}
                    maxLength={40}
                  />
                  {/* {location && !locationError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLocation("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {locationError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )} */}
                  {locationList.length > 0 ? (
                    <div className="location-result-wrap mw-100 mt-4">
                      <ul>
                        {locationList?.map((address, i) => {
                          return (
                            <li key={i}>
                              <a
                                role="button"
                                href={void 0}
                                onClick={(e) => {
                                  handleItemSelection(address);
                                }}
                              >
                                {_.get(address, "formatted_address", "")}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="mob-error-msg">{locationError}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-0">
                  <label className="form-label">
                    Company/Agency <span>*</span>
                  </label>

                  <TextBox
                    name="company"
                    type="text"
                    notShowError={true}
                    value={toTitleCase(company)}
                    placeholder="Company or agency name"
                    onChange={(e) => handleCompany(e.target.value)}
                    className={
                      companyError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    maxLength={50}
                  />
                  {company && !companyError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setCompany("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {companyError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{companyError}</div>
                </div>
              </div>
              <div
                className={`col-12 upload-btn-innerwrap addyour-property-details-wrap mb-12`}
              >
                <label
                  htmlFor="logoUrl"
                  className={`active agent-user-btn`}
                >
                  Add Logo
                </label>
                <input
                  type="file"
                  name="logoUrl"
                  id="logoUrl"
                  onChange={(e) => handleImageUpload(e, true)}
                />
                <span className="error-msg w-104 h-auto error-wordbreak">
                  {logoError}
                </span>
              </div>
              <div className="col-12">
                <div className="mob-fld-wrap position-relative mb-2">
                  <label className="form-label">
                    License number <span>*</span>
                  </label>

                  <TextBox
                    name="company"
                    type="text"
                    notShowError={true}
                    className={
                      licenseError
                        ? "form-control mob-pr-44 validation-alert-brdr"
                        : "form-control mob-pr-44"
                    }
                    value={toTitleCase(license)}
                    placeholder="License number"
                    onChange={(e) => handleLicenseChange(e.target.value)}
                    maxLength={40}
                  />
                  {license && !licenseError && (
                    <Button
                      variant=""
                      className="fld-close-btn"
                      onClick={() => setLicense("")}
                    >
                      <img src={FldCloseIcon} />
                    </Button>
                  )}
                  {licenseError && (
                    <div className="validation-alert">
                      <img src={ValidationAlertIcon} />
                    </div>
                  )}

                  <div className="mob-error-msg">{licenseError}</div>
                </div>
              </div>
            </div>
            <div className="btn-wrap text-center pb-4 pt-2">
              <button
                type="button"
                className="btn btn-primary w-100 mb-0"
                disabled={checkValid()}
                onClick={() => handleAgentDetailsSave(agentInfo)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      <ProfileImageCropper
        userId={userId}
        image={changedImage}
        show={showPofileImageUploadModal}
        onHide={() => {
          ShowPofileImageUploadModalClose();
        }}
        isSquare={isSquareImage}
        updateImageAfterCrop={(imageUrl) => {
          if (isSquareImage) {
            setLogoUrl(imageUrl);
          } else {
            setImageUrl(imageUrl);
          }
          ShowPofileImageUploadModalClose();
        }}
      />
    </div>
  );
};

export default AgentProfileDefault;
